import type { ErrorEvent } from '@sentry/react';
import * as Sentry from '@sentry/react';

/**
 * Returns true if the Sentry event is an xterm-internal error that should
 * be dropped. xterm 6.0.0 has a minification/tree-shaking bug where a
 * reference to a removed variable surfaces as a ReferenceError during
 * DECRQM escape sequence parsing. These errors are non-actionable and
 * originate entirely within the third-party library.
 */
export function isXtermInternalError(event: ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  if (exception?.type !== 'ReferenceError') return false;

  const frames = exception.stacktrace?.frames;
  if (!frames?.length) return false;

  return frames.every((f) => f.filename?.includes('@xterm/xterm') || f.module?.includes('@xterm/xterm'));
}

/**
 * Returns true if the error is a browser-level network failure (DNS
 * resolution, connection refused, offline, tab backgrounded, navigation
 * away) rather than an application bug. These surface as `TypeError:
 * Failed to fetch` in Chromium-based browsers and `TypeError:
 * NetworkError when attempting to fetch resource` in Firefox. They are
 * transient and not actionable in Sentry.
 */
export function isNetworkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;
  const msg = error.message.toLowerCase();
  return msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('network request failed');
}

interface BreadcrumbOptions {
  category: string;
  message: string;
  data?: Record<string, unknown>;
}

/**
 * Report a caught error to Sentry, downgrading browser-level network
 * failures to breadcrumbs so they do not create actionable exceptions.
 *
 * Use this in every catch block that wraps a network API call where a
 * transient connectivity failure is expected and non-actionable
 * (workspace lifecycle, fire-and-forget persistence, etc.).
 *
 * :param error: The caught error.
 * :param breadcrumb: Category/message/data used when the error is a
 *     network failure.
 * :param captureContext: Tags/fingerprint forwarded to
 *     `Sentry.captureException` for non-network errors.
 */
export function captureOrBreadcrumbNetworkError(
  error: unknown,
  breadcrumb: BreadcrumbOptions,
  captureContext?: Parameters<typeof Sentry.captureException>[1]
): void {
  if (isNetworkError(error)) {
    Sentry.addBreadcrumb({
      category: breadcrumb.category,
      message: breadcrumb.message,
      level: 'warning',
      data: {
        ...breadcrumb.data,
        error: error instanceof Error ? error.message : String(error),
      },
    });
    return;
  }

  Sentry.captureException(error, captureContext);
}
